import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { bcbsPlanSchema } from "~/schemas/SignUp/InsuranceProvider";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { getBcbsProviders } from "~/utils";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

// Matches the bcbsProvider value that gates the ANTHEM_EMPIRE_POS step in insuranceFlows.json.
const ANTHEM_BCBS_PLAN = "Anthem";

export const BcbsPlanForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: InsuranceProviderFormProps & { stepId?: string }) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const currentInsurance = isSecondaryProvider
    ? secondaryInsurance
    : primaryInsurance;

  const planTypeProvider = currentInsurance?.planTypeProvider;
  const planCoverage = currentInsurance?.planCoverage;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.bcbsProvider
    : primaryInsurance.bcbsProvider;

  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "bcbs_plan",
  });

  // Get BCBS providers from config (conditional by planType and planCoverage)
  const bcbsProviders = getBcbsProviders(planTypeProvider, planCoverage);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof bcbsPlanSchema>>({
    mode: "onBlur",
    resolver: zodResolver(bcbsPlanSchema),
    defaultValues: {
      bcbsPlan: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setSelectedValue(defaultValue ?? "");
    setValue("bcbsPlan", defaultValue ?? "");
  }, [defaultValue, setValue]);

  // Reset selection if plan type/coverage changes and current selection is invalid
  useEffect(() => {
    const validValues = bcbsProviders.map((p) => p.value);
    if (defaultValue && !validValues.includes(defaultValue)) {
      setSelectedValue("");
      setValue("bcbsPlan", "");
      setPatientInsuranceField("bcbsProvider", undefined, isSecondaryProvider);
    }
  }, [
    planTypeProvider,
    planCoverage,
    bcbsProviders,
    defaultValue,
    setValue,
    setPatientInsuranceField,
    isSecondaryProvider,
  ]);

  const handleSelectionChange = (value: string) => {
    setSelectedValue(value);
    setValue("bcbsPlan", value, { shouldValidate: true });
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof bcbsPlanSchema>> = (data) => {
    setPatientInsuranceField(
      "bcbsProvider",
      data.bcbsPlan,
      isSecondaryProvider,
    );
    // Empire POS is only asked about Anthem cards. A patient who answered it and then
    // changed their plan must not carry that answer into a payer it says nothing about.
    if (data.bcbsPlan !== ANTHEM_BCBS_PLAN) {
      setPatientInsuranceField(
        "anthemEmpirePos",
        undefined,
        isSecondaryProvider,
      );
    }
    trackComplete({ bcbs_plan: data.bcbsPlan });

    (onNext as (response?: string) => void)(data.bcbsPlan);
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("bcbsProvider", undefined, isSecondaryProvider);
    trackBack();
    onPrev();
  };

  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="w-full max-w-[570px] rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          Which BCBS plan do you have?
        </h1>
        <RadioCardGroup
          options={bcbsProviders}
          value={selectedValue}
          onChange={handleSelectionChange}
        />
        {errors.bcbsPlan && (
          <ErrorMessage message={errors.bcbsPlan.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select a plan to continue"
      />
    </form>
  );
};
